import axios, { AxiosResponse, ResponseType } from "axios";
import { APIError, AuthError, NotFoundError } from "./error_service";
import { AppHeaders } from "@/models/api/api";
import { Order, OrderRecord } from "@/models/order/order";

axios.defaults.headers = { apikey: process.env.VUE_APP_API_KEY } as AppHeaders;
axios.defaults.baseURL = process.env.VUE_APP_API_NODE_URL;

/**
 * Creates a new Order Service

 * @example
 * const orderService = new OrderService();
 * orderService.getSingleOrder();
 */

export default class ProductService {
  public async getSingleOrder(
    id: string,
    duplicate?: boolean
  ): Promise<OrderRecord> {
    let order;
    let url = "/orders/" + id;
    if (duplicate) {
      url += "?duplicate=true";
    }
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.get(url, options);
      order = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return order;
  }

  public async downloadOrderInvoice(id: string): Promise<Blob> {
    let order;
    const url = "/orders/" + id + "?download=true";
    try {
      //https://www.linkedin.com/pulse/converting-pdf-binary-base64-nodejs-trick-end-andrew-ribeiro
      const options = {
        responseType: "arraybuffer" as ResponseType,
        responseEncoding: "binary",
        headers: {
          "Content-Type": "application/pdf"
        }
      };
      const res: AxiosResponse = await axios.get(url, options);
      order = res.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    order = new Blob([new Uint8Array(order).buffer]);
    return order;
  }

  public async deleteOrder(id: string): Promise<Order> {
    let order;
    const url = "/orders/" + id;
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.delete(url, options);
      order = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return order;
  }

  public async updateOrder(id: string, body: Order): Promise<Order> {
    let order;
    const url = "/orders/" + id;
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.put(url, body, options);
      order = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return order;
  }

  public async createOrder(body: Order): Promise<Order> {
    let order;
    const url = "/orders";
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.post(url, body, options);
      order = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return order;
  }

  public async convertOrderToQuote(id: string, body: Order): Promise<Order> {
    body._id = null;
    let order;
    const url = "/orders?convert=true&quoteId=" + id;
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.post(url, body, options);
      order = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return order;
  }

  public async getCustomerOrders(customerId: string): Promise<Order[]> {
    let orders;
    const url = "/orders/customers/" + customerId;
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.get(url, options);
      const order = res.data.data.orders;
      const quote = res.data.data.quotes;
      orders = order.concat(quote);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return orders;
  }

  public async downloadQuoteInvoice(id: string): Promise<Blob> {
    let quote;
    const url = "/orders/quotes/" + id + "?download=true";
    try {
      //https://www.linkedin.com/pulse/converting-pdf-binary-base64-nodejs-trick-end-andrew-ribeiro
      const options = {
        responseType: "arraybuffer" as ResponseType,
        responseEncoding: "binary",
        headers: {
          "Content-Type": "application/pdf"
        }
      };
      const res: AxiosResponse = await axios.get(url, options);
      quote = res.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    quote = new Blob([new Uint8Array(quote).buffer]);
    return quote;
  }

  public async getSingleQuote(
    id: string,
    duplicate?: boolean
  ): Promise<OrderRecord> {
    let quote;
    let url = "/orders/quotes/" + id;
    if (duplicate) {
      url += "?duplicate=true";
    }
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.get(url, options);
      quote = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return quote;
  }

  public async deleteQuote(id: string): Promise<Order> {
    let quote;
    const url = "/orders/quotes/" + id;
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.delete(url, options);
      quote = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return quote;
  }

  public async updateQuote(id: string, body: Order): Promise<Order> {
    let quote;
    const url = "/orders/quotes/" + id;
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.put(url, body, options);
      quote = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return quote;
  }

  public async createQuote(body: Order): Promise<Order> {
    let quote;
    const url = "/orders/quotes";
    try {
      const options = {
      };
      const res: AxiosResponse = await axios.post(url, body, options);
      quote = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Something went wrong", err.response.status);
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return quote;
  }
}
